import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { colorLabel, type RelationType, type RelationTypeColor } from '@kontor/shared';
import { api, ApiError } from '../../api';
import { useSession } from '../../session';
import { ColorPicker } from '../../components/color/ColorPicker';
import { toneClass } from '../../components/color/ColorSwatches';

/** Bereich „Beziehungsarten“ der Einrichtung (KON-104). Aufgebaut wie die Terminarten. Die Rechte prüft der Server, die Oberfläche blendet nur aus. */
export function RelationTypesPage() {
  const { can } = useSession();
  const client = useQueryClient();
  const canWrite = can('setup.write');
  const canDelete = can('setup.delete');

  const types = useQuery({ queryKey: ['contact-relation-types', 'manage'], queryFn: async () => (await api<{ data: RelationType[] }>('GET', '/contact-relation-types/manage')).data });
  const refresh = () => client.invalidateQueries({ queryKey: ['contact-relation-types'] });

  const [name, setName] = useState('');
  const [color, setColor] = useState<string>('#0f766e');
  const [createErrors, setCreateErrors] = useState<Record<string, string>>({});
  const [editing, setEditing] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editColor, setEditColor] = useState<string>('#0f766e');
  const [editErrors, setEditErrors] = useState<Record<string, string>>({});

  const create = useMutation({
    mutationFn: () => api('POST', '/contact-relation-types', { name: name.trim(), color }),
    onSuccess: () => {
      setName('');
      setCreateErrors({});
      refresh();
    },
    onError: (e) => setCreateErrors(e instanceof ApiError && e.status === 422 ? e.fields : { name: e instanceof ApiError ? e.message : 'Die Beziehungsart konnte nicht angelegt werden.' }),
  });
  const update = useMutation({
    mutationFn: (id: string) => api('PATCH', `/contact-relation-types/${id}`, { name: editName.trim(), color: editColor }),
    onSuccess: () => {
      setEditing(null);
      setEditErrors({});
      refresh();
    },
    onError: (e) => setEditErrors(e instanceof ApiError && e.status === 422 ? e.fields : { name: e instanceof ApiError ? e.message : 'Die Beziehungsart konnte nicht geändert werden.' }),
  });
  const reorder = useMutation({
    mutationFn: (ids: string[]) => api('POST', '/contact-relation-types/order', { ids }),
    onSuccess: refresh,
  });
  const remove = useMutation({
    mutationFn: (id: string) => api('DELETE', `/contact-relation-types/${id}`),
    onSuccess: refresh,
  });

  const list = types.data ?? [];
  const move = (index: number, delta: -1 | 1) => {
    const ids = list.map((t) => t.id);
    const target = index + delta;
    if (target < 0 || target >= ids.length) return;
    [ids[index], ids[target]] = [ids[target]!, ids[index]!];
    reorder.mutate(ids);
  };
  const startEdit = (t: RelationType) => {
    setEditing(t.id);
    setEditName(t.name);
    setEditColor(t.color);
    setEditErrors({});
  };
  const error = [reorder.error, remove.error, types.error].find((e) => e instanceof ApiError) as ApiError | undefined;

  function submit(event: FormEvent) {
    event.preventDefault();
    setCreateErrors({});
    create.mutate();
  }

  return (
    <section className="card">
      <h2>Beziehungsarten</h2>
      <p className="muted small">
        In welcher Beziehung Ihr Unternehmen zu einem Kontakt steht, zum Beispiel „Kunde“, „Partner“, „Lieferant“ oder „Externer Dienstleister“. Im Kontakt wählt man eine
        oder mehrere Arten aus, und die Kontaktliste lässt sich danach filtern. Die Farbe hilft, die Arten in der Liste auseinanderzuhalten.
      </p>

      {error && <div className="alert alert--error" role="alert">{error.message}</div>}

      {canWrite && (
        <form className="type-form" onSubmit={submit} noValidate>
          <label className="field">
            <span>Neue Art</span>
            <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Zum Beispiel Subunternehmer" maxLength={60} autoComplete="off" aria-invalid={!!createErrors.name} />
            {createErrors.name && <small className="field__error" role="alert">{createErrors.name}</small>}
          </label>
          <div className="field">
            <span>Farbe</span>
            <ColorPicker value={color} onChange={setColor} label="Farbe der neuen Art" />
          </div>
          <button className="btn btn--primary type-form__submit" type="submit" disabled={create.isPending || name.trim().length < 2}>
            {create.isPending ? 'Wird angelegt …' : 'Art anlegen'}
          </button>
        </form>
      )}

      <table className="table table--cards">
        <thead>
          <tr>
            <th>Art</th>
            <th>Farbe</th>
            <th>Kontakte</th>
            <th><span className="sr-only">Aktionen</span></th>
          </tr>
        </thead>
        <tbody>
          {types.isLoading && <tr><td colSpan={4} className="muted">Wird geladen …</td></tr>}
          {!types.isLoading && list.length === 0 && <tr><td colSpan={4} className="empty">Noch keine Beziehungsarten angelegt. Kontakte funktionieren auch ohne.</td></tr>}
          {list.map((t, index) => {
            const isEditing = editing === t.id;
            return (
              <tr key={t.id}>
                <td>
                  {isEditing ? (
                    <>
                      <input className="input" value={editName} onChange={(e) => setEditName(e.target.value)} maxLength={60} aria-label={`Bezeichnung von ${t.name}`} aria-invalid={!!editErrors.name} />
                      {editErrors.name && <small className="field__error" role="alert">{editErrors.name}</small>}
                    </>
                  ) : (
                    <span className="type-name"><i className={`type-swatch ${toneClass(t.color)}`} aria-hidden="true" />{t.name}</span>
                  )}
                </td>
                <td data-label="Farbe">
                  {isEditing ? (
                    <ColorPicker value={editColor} onChange={setEditColor} label={`Farbe von ${t.name}`} />
                  ) : (
                    <span className={`type-badge ${toneClass(t.color)}`}>
                      {colorLabel(t.color)}
                    </span>
                  )}
                </td>
                <td data-label="Kontakte">{t.usageCount}</td>
                <td className="cell-actions">
                  <div className="row-actions">
                    {canWrite && isEditing && (
                      <>
                        <button type="button" className="btn btn--primary btn--sm" disabled={update.isPending} onClick={() => update.mutate(t.id)}>Speichern</button>
                        <button type="button" className="btn btn--secondary btn--sm" onClick={() => setEditing(null)}>Abbrechen</button>
                      </>
                    )}
                    {canWrite && !isEditing && (
                      <>
                        <button type="button" className="btn btn--secondary btn--sm" aria-label={`${t.name} nach oben`} disabled={index === 0 || reorder.isPending} onClick={() => move(index, -1)}>↑</button>
                        <button type="button" className="btn btn--secondary btn--sm" aria-label={`${t.name} nach unten`} disabled={index === list.length - 1 || reorder.isPending} onClick={() => move(index, 1)}>↓</button>
                        <button type="button" className="btn btn--secondary btn--sm" onClick={() => startEdit(t)}>Bearbeiten</button>
                      </>
                    )}
                    {canDelete && !isEditing && (
                      <button
                        type="button"
                        className="btn btn--danger btn--sm"
                        disabled={remove.isPending}
                        onClick={() => {
                          if (window.confirm(`Beziehungsart „${t.name}“ wirklich löschen?`)) remove.mutate(t.id);
                        }}
                      >
                        Löschen
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <p className="muted small">Eine Beziehungsart, die Kontakten zugeordnet ist, lässt sich nicht löschen. Benennen Sie sie stattdessen um, oder nehmen Sie sie zuerst aus den Kontakten heraus.</p>
    </section>
  );
}
